import debounce from '../../../assets/js/base/_debounce.js';

export function getFocusableNodes(
	scope = document,
	selector = 'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"]'
) {
	let nodes = Array.from( scope.querySelectorAll( selector ) ).filter( ( node ) => node.offsetParent !== null );

	return nodes;
}

export function getFirstFocusableNode( scope = document ) {
	const nodes = getFocusableNodes( scope );

	return nodes[ 0 ] ?? null;
}

export function focusWithin( node, opts = { preventScroll: true } ) {
	const child = getFirstFocusableNode( node );

	if ( child ) {
		child.focus( opts );

		return child;
	}

	return null;
}

export function requestAnimationFrame( fn ) {
	return window.requestAnimationFrame( () => window.requestAnimationFrame( fn ) );
}

export class GlobalNav {
	constructor( node ) {
		this.node = node;
		this.store = {};
		this.activeClass = 'global-nav__item--active';
		this.contentActiveClass = 'global-nav__content--active';
		this.coverActiveClass = 'global-nav__cover--active';
		this.closer = this.node.querySelector( '.global-nav__close' );
		this.actionSearch = this.node.querySelector( '.global-nav__action--search' );
		this.quote = this.node.querySelector( '.global-nav__quote' );
		this.primary = this.node.querySelector( '.global-nav__column--primary' );
		this.primaryIndicator = this.primary.querySelector( '.global-nav__indicator' );
		this.secondary = this.node.querySelector( '.global-nav__column--secondary' );
		this.secondaryIndicator = this.secondary.querySelector( '.global-nav__indicator' );
		this.tertiary = this.node.querySelector( '.global-nav__column--tertiary' );
		this.opened = false;
		this.mode = 'menu';
		this.modes = [
			'menu',
			'search',
		];
		this.previousActiveElement = null;
		this.lastKeydown = null;
		this.isScrolling = false;

		this.active = {
			primary: null,
			secondary: null,
		};

		for ( let item of this.node.querySelectorAll( '.global-nav__item' ) ) {
			const parent = item.closest( '.global-nav__content' );
			const title = item.querySelector( '.global-nav__item-text' )?.textContent ?? '';
			const stepParent = parent ? node.querySelector( `.global-nav__item[data-uuid="${ parent.dataset.parent }"]` ) : null;
			const child = node.querySelector( `.global-nav__content[data-parent="${ item.dataset.uuid }"]` );
			const cover = node.querySelector( `.global-nav__cover[data-parent="${ item.dataset.uuid }"]` );
			const type = item.closest( '.global-nav__column--primary' ) ? 'primary' : item.closest( '.global-nav__column--secondary' ) ? 'secondary' : null;
			const back = child ? child.querySelector( '.global-nav__content-back' ) : null;
			const arrow = item.querySelector( '.global-nav__item-arrow' );
			const proxy = item.dataset.proxy ?? null;
			const hash = item.dataset.hash ?? null;

			if ( title && back ) {
				back.querySelector( '.global-nav__content-back-text' ).textContent = title;
			}

			this.store[ item.dataset.uuid ] = {
				item,
				title,
				child,
				cover,
				parent,
				stepParent,
				proxy,
				type,
				back,
				hash,
				arrow,
				active: false,
			};
		}

		const handleClick = ( e ) => {
			let target = e.target;
			this.lastKeydown = null;

			while ( target && target.parentNode && target !== this.node ) {
				if ( target.matches( '.global-nav__close' ) ) {
					e.preventDefault();
					return this.close();
				}

				if ( target.matches( '.global-nav__column--tertiary .global-nav__content-back' ) ) {
					e.preventDefault();
					return this.deactivate( this.active.secondary );
				}

				if ( target.matches( '.global-nav__column--secondary .global-nav__content-back' ) ) {
					e.preventDefault();

					if ( this.active.secondary ) {
						this.deactivate( this.active.secondary );
					}

					return this.deactivate( this.active.primary );
				}

				if ( target.matches( '.global-nav__item' ) ) {
					const { uuid } = target.dataset;
					const { child, proxy } = this.store[ uuid ];

					if ( ( uuid && child ) || proxy ) {
						e.preventDefault();
						return this.activate( uuid );
					}
				}

				target = target.parentNode;
			}
		};

		const handleBlur = ( e ) => {
			if ( ! e.target.matches( '.global-nav__content .global-nav__item > .global-nav__link' ) ) {
				return;
			}

			const stepParent = e.target.closest( '.global-nav__content' );
			const item = e.target.closest( '.global-nav__item' );

			if ( ! stepParent.dataset.parent ) {
				return;
			}

			const parent = this.node.querySelector( `.global-nav__item[data-uuid="${ stepParent.dataset.parent }"]` );

			if ( item.matches( ':first-child' ) && this.lastKeydown && this.lastKeydown.shiftKey && this.lastKeydown.key === 'Tab' ) {
				// focus the parent on shift+tab on first link
				focusWithin( parent );
			}

			if (
				item.matches( ':last-child' ) &&
				this.lastKeydown &&
				! this.lastKeydown.shiftKey &&
				this.lastKeydown.key === 'Tab' &&
				parent.nextElementSibling
			) {
				// focus the parents next sibling when a column loses focus with forward tab
				focusWithin( parent.nextElementSibling );
			}
		};

		const handleKeydown = ( e ) => {
			this.lastKeydown = e;
		};

		const handleKeyup = ( e ) => {
			let target = e.target;

			while ( target && target !== this.node ) {
				if (
					[
						'INPUT',
						'TEXTAREA',
					].includes( e.target.tagName )
				) {
					return;
				}

				target = target.parentNode;
			}

			if ( target === this.node ) {
				switch ( e.key ) {
					case 'Escape':
						this.close();
						break;
				}
			}
		};

		const handleFocus = ( e ) => {
			if (
				e.target.matches( '.global-nav__column--primary .global-nav__item > .global-nav__link' ) &&
				this.lastKeydown &&
				this.lastKeydown.key === 'Tab' &&
				this.active.primary
			) {
				// deactivate items when a primary item is tabbed to - avoids a lot of weird tab scenarios
				const item = e.target.closest( '.global-nav__item' );

				if ( item.dataset.uuid !== this.active.primary ) {
					this.deactivate( this.active.primary );

					if ( this.active.secondary ) {
						this.deactivate( this.active.secondary );
					}
				}
			}
		};

		const handleActivate = ( e ) => {
			let { hash } = e.detail;

			if ( ! hash ) {
				return;
			}

			hash = hash.replace( /^#/, '' );

			for ( const uuid in this.store ) {
				const item = this.store[ uuid ];

				if ( item.hash === hash ) {
					this.open( 'menu' );
					this.activate( uuid );
					return;
				}
			}
		};

		const handleScroll = () => {
			this.isScrolling = true;
			this.node.classList.add( 'global-nav--is-scrolling' );
			this.moveIndicators();
		};

		const handleScrollEnd = () => {
			this.isScrolling = false;
			this.node.classList.remove( 'global-nav--is-scrolling' );
		};

		this.node.addEventListener( 'click', handleClick );
		this.node.addEventListener( 'blur', handleBlur, true );
		this.node.addEventListener( 'focus', handleFocus, true );
		this.node.addEventListener( 'keydown', handleKeydown, true );
		this.node.addEventListener( 'keyup', handleKeyup, true );
		this.node.addEventListener( 'scroll', handleScroll, true );
		this.node.addEventListener( 'scroll', debounce( handleScrollEnd, 200 ), true );
		this.node.addEventListener( 'transitionend', this.moveIndicators.bind( this ) );

		document.body.addEventListener( 'global-nav:open', () => this.open() );
		document.body.addEventListener( 'global-nav:close', () => this.close() );
		document.body.addEventListener( 'global-nav:menu', () => this.open( 'menu' ) );
		document.body.addEventListener( 'global-nav:search', () => this.open( 'search' ) );
		document.body.addEventListener( 'global-nav:activate', handleActivate.bind( this ) );

		window.addEventListener( 'resize', this.moveIndicators.bind( this ) );

		this.setMode( 'menu' );
		setTimeout( () => this.node.classList.add( 'global-nav--ready' ), 1 );
	}

	moveIndicators() {
		const activeClass = 'global-nav__indicator--active';

		if ( this.active.primary ) {
			const { arrow } = this.store[ this.active.primary ];
			const { top } = arrow.getBoundingClientRect();

			this.primaryIndicator.style.top = `${ top }px`;

			if ( ! this.primaryIndicator.classList.contains( activeClass ) ) {
				setTimeout( () => this.primaryIndicator.classList.add( activeClass ), 400 );
			}
		} else {
			this.primaryIndicator.classList.remove( activeClass );
		}

		if ( this.active.secondary ) {
			const { arrow } = this.store[ this.active.secondary ];
			const { top } = arrow.getBoundingClientRect();

			this.secondaryIndicator.style.top = `${ top }px`;

			if ( ! this.secondaryIndicator.classList.contains( activeClass ) ) {
				setTimeout( () => this.secondaryIndicator.classList.add( activeClass ), 400 );
			}
		} else {
			this.secondaryIndicator.classList.remove( activeClass );
		}
	}

	activate( uuid ) {
		if ( ! uuid || ! this.store[ uuid ] ) {
			return;
		}

		if ( uuid === this.active.primary ) {
			this.active.primary = null;
			this.active.secondary = null;

			return this.deactivate( uuid );
		}

		if ( uuid === this.active.secondary ) {
			this.active.secondary = null;

			return this.deactivate( uuid );
		}

		const { type, item, child, cover, proxy } = this.store[ uuid ];
		const primary = this.active.primary ? this.store[ this.active.primary ] : null;
		const secondary = this.active.secondary ? this.store[ this.active.secondary ] : null;

		if ( proxy && this.store[ proxy ] ) {
			if ( this.store[ proxy ].stepParent ) {
				this.activate( this.store[ proxy ].stepParent.dataset.uuid );
			}

			return this.activate( this.store[ uuid ].proxy );
		}

		if ( 'primary' === type ) {
			if ( primary ) {
				primary.item.classList.remove( this.activeClass );
				primary.child.classList.remove( this.contentActiveClass );
				primary.child.hidden = true;

				if ( primary.cover ) {
					primary.cover.classList.remove( this.coverActiveClass );
				}
			}

			if ( secondary ) {
				secondary.item.classList.remove( this.activeClass );
				secondary.child.classList.remove( this.contentActiveClass );
				secondary.child.hidden = true;
			}

			this.active.primary = uuid;
			this.active.secondary = null;

			child.removeAttribute( 'hidden' );
			requestAnimationFrame( () => {
				item.classList.add( this.activeClass );
				child.classList.add( this.contentActiveClass );
				focusWithin( child );

				if ( cover ) {
					cover.classList.add( this.coverActiveClass );
				}
			} );
		} else if ( 'secondary' === type ) {
			if ( secondary ) {
				secondary.item.classList.remove( this.activeClass );
				secondary.child.classList.remove( this.contentActiveClass );
				secondary.child.hidden = true;
			}

			this.active.secondary = uuid;

			child.removeAttribute( 'hidden' );

			requestAnimationFrame( () => {
				item.classList.add( this.activeClass );
				child.classList.add( this.contentActiveClass );
				focusWithin( child );
			} );
		}

		this.setState();
		this.moveIndicators();
		this.dispatchEvent( 'global-nav:activated' );
	}

	setState() {
		if ( this.active.primary || this.active.secondary ) {
			this.node.classList.add( 'global-nav--active' );
		} else {
			this.node.classList.remove( 'global-nav--active' );
		}

		if ( this.active.primary ) {
			this.node.classList.add( 'global-nav--primary-active' );
			this.primary.classList.add( 'global-nav__column--active' );
		} else {
			this.node.classList.remove( 'global-nav--primary-active' );
			this.primary.classList.remove( 'global-nav__column--active' );
		}

		if ( this.active.secondary ) {
			this.node.classList.add( 'global-nav--secondary-active' );
			this.secondary.classList.add( 'global-nav__column--active' );
		} else {
			this.node.classList.remove( 'global-nav--secondary-active' );
			this.secondary.classList.remove( 'global-nav__column--active' );
		}
	}

	deactivate( uuid ) {
		if ( ! uuid || ( typeof uuid === 'string' && ! this.store[ uuid ] ) ) {
			return;
		}

		const { item, child, cover } = this.store[ uuid ];

		if ( this.active.primary === uuid ) {
			this.active.primary = null;
		}

		if ( this.active.secondary === uuid ) {
			this.active.secondary = null;
		}

		item.classList.remove( this.activeClass );
		child.classList.remove( this.contentActiveClass );
		child.hidden = true;

		if ( cover ) {
			cover.classList.remove( this.coverActiveClass );
		}

		this.setState();
		this.moveIndicators();
		this.dispatchEvent( 'global-nav:deactivated' );
	}

	setMode( mode ) {
		if ( ! this.modes.includes( mode ) ) {
			return;
		}

		if ( this.mode ) {
			this.node.classList.remove( `global-nav--${ this.mode }` );
		}

		this.mode = mode;
		this.node.classList.add( `global-nav--${ this.mode }` );

		this.dispatchEvent( `global-nav:mode:${ this.mode }` );
	}

	open( mode ) {
		if ( mode ) {
			this.setMode( mode );
		}

		this.node.classList.add( 'global-nav--open' );
		document.body.classList.add( 'has-global-nav' );
		this.opened = true;

		const close = this.node.querySelector( '.global-nav__action--close' );

		if ( close ) {
			close.focus();
		}

		this.moveIndicators();
		this.dispatchEvent( 'global-nav:opened' );
	}

	close() {
		document.body.classList.remove( 'has-global-nav' );
		this.deactivate( this.active.primary );
		this.deactivate( this.active.secondary );
		this.node.classList.remove( 'global-nav--open', 'global-nav--active' );
		this.opened = false;

		this.dispatchEvent( 'global-nav:closed' );
	}

	toggle() {
		return this.opened ? this.close() : this.open();
	}

	dispatchEvent( name, detail = {} ) {
		return this.node.dispatchEvent(
			new CustomEvent( name, {
				bubbles: true,
				cancelable: false,
				detail: {
					...detail,
					controller: this,
				},
			} )
		);
	}
}

export default new GlobalNav( document.querySelector( '.global-nav' ) );
